import React, { useEffect, useState } from "react";
import {
  deleteRestaurant,
  getAllRestaurants,
} from "../../../api/restaurants.api";
import AdminLayout from "../../layout/AdminLayout";
import { BsTrash3, BsPencil, BsEye } from "react-icons/bs";
import { Link } from "react-router-dom";
import {
  confirmRestaurantDelete,
  errorMsg,
  success,
} from "../../../utils/notifications";
import { FiPlus } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import {
  FaUtensils,
  FaChair,
  FaWalking,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaEuroSign,
  FaTag,
} from "react-icons/fa";

function Restaurants() {
  const [restaurantDetails, setRestaurantDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    getAllRestaurants()
      .then((res) => {
        if (res?.data) {
          setRestaurantDetails(res.data);
          setLoading(false);
        } else {
          setRestaurantDetails([]);
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching data:", error);
      });
  }, []);

  const handleDelete = (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    confirmRestaurantDelete().then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteRestaurant(id);
          setRestaurantDetails(
            restaurantDetails.filter((restaurant) => restaurant._id !== id)
          );
          success("Success!", "Fixed Location deleted successfully!");
        } catch (error) {
          console.error("Error deleting restaurant:", error);
          errorMsg("Oops", error?.response?.message);
        }
      }
    });
  };

  const filteredRestaurants = restaurantDetails.filter(
    (restaurant) =>
      restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      restaurant.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Fixed Locations">
      <div className="p-5">
        <div className="mb-5 flex flex-col md:flex-row items-center justify-between sticky top-16 bg-slate-50 py-3 z-10 px-2 gap-4">
          <div className="w-full md:w-auto">
            <input
              type="text"
              placeholder="Search locations..."
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Link
            to="/fixedLocations/add"
            className="flex items-center justify-center rounded-md bg-yellow-500 px-4 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-yellow-600 transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500 whitespace-nowrap"
          >
            <FiPlus className="text-lg mr-2" />
            Add Fixed Location
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RotatingLines
              strokeColor="black"
              strokeWidth="5"
              animationDuration="0.75"
              width="96"
              visible={true}
            />
          </div>
        ) : (
          <>
            {filteredRestaurants.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No locations found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm
                    ? "No locations match your search criteria"
                    : "You haven't added any fixed locations yet"}
                </p>
                <Link
                  to="/fixedLocations/add"
                  className="inline-flex items-center px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors"
                >
                  <FiPlus className="mr-2" />
                  Add Your First Location
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRestaurants.map((restaurant) => (
                  <div
                    key={restaurant._id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-gray-800 truncate">
                          {restaurant.name}
                        </h3>
                        <div className="flex space-x-2">
                          <Link
                            to={`/fixedLocations/${restaurant._id}`}
                            className="text-gray-600 hover:text-yellow-600 transition-colors p-1"
                            title="View"
                          >
                            <BsEye size={18} />
                          </Link>
                          <Link
                            to={`/fixedLocations/edit/${restaurant._id}`}
                            className="text-gray-600 hover:text-blue-600 transition-colors p-1"
                            title="Edit"
                          >
                            <BsPencil size={18} />
                          </Link>
                          <button
                            onClick={(e) => handleDelete(restaurant._id, e)}
                            className="text-gray-600 hover:text-red-600 transition-colors p-1"
                            title="Delete"
                          >
                            <BsTrash3 size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="text-yellow-500 mr-2" />
                          <span className="text-gray-600">
                            {restaurant.location || "Location not specified"}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FaUtensils className="text-yellow-500 mr-2" />
                            <span className="text-gray-600">
                              {restaurant.cuisineType || "N/A"}
                            </span>
                          </div>

                          <div className="flex items-center">
                            <FaTag className="text-yellow-500 mr-2" />{" "}
                            {/* Price Tag Icon */}
                            <span className="text-gray-600">
                              {restaurant.basePricePerWeek || "0"} €
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FaChair className="text-yellow-500 mr-2" />
                            <span className="text-gray-600">
                              {restaurant.seatingCapacity || "0"} seats
                            </span>
                          </div>
                          <div className="flex items-center">
                            <FaWalking className="text-yellow-500 mr-2" />
                            <span className="text-gray-600">
                              {restaurant.footTrafficEstimate || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              restaurant.status === "Pending Approval"
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                            }`}
                          >
                            {restaurant.status}
                          </span>
                        </div>

                        <div className="pt-3 border-t border-gray-100">
                          <h4 className="text-sm font-semibold text-gray-500 mb-2">
                            Contact Information
                          </h4>
                          <div className="flex items-center">
                            <FaPhone className="text-gray-400 mr-2" />
                            <span className="text-gray-600">
                              {restaurant.contactInformation?.phone || "N/A"}
                            </span>
                          </div>
                          <div className="flex items-center mt-1">
                            <FaEnvelope className="text-gray-400 mr-2" />
                            <span className="text-gray-600 truncate">
                              {restaurant.contactInformation?.email || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default Restaurants;
